/**
 * User History report — several people, one date range, two sources.
 *
 * Distinct from userHistory.service (the breadcrumb modal: one user, trailing few
 * days) and activity.service (the modal's other tab). Those stay as they are; this
 * is the supervisory view and talks to its own endpoints.
 */
import api from './api';

export interface ReportUser {
  /** AD object id, or `v1:<username>` for someone with a V1 login but no Azure
   *  account — about half the dispatcher roster and a tenth of the larger LC groups. */
  user_id: string;
  display_name: string;
  email: string | null;
  v1_username: string | null;
  active?: boolean;
}

/** An LC team or dispatcher roster. `value` is its members joined by '~', the same
 *  convention the dispatch pages use; the backend expands it, so selecting a group
 *  costs one option here and the API still only ever sees a list of people. */
export interface ReportGroup {
  label: string;
  value: string;
  count: number;
}

export interface ReportUsersResponse {
  users: ReportUser[];
  groups: ReportGroup[];
}

/** A record change — cms.history. Field-level, durable. */
export interface HistoryReportRow {
  recno: number;
  dbtable: string;
  keyvalue: string;
  datetime: string;
  userid: string;
  colname: string;
  oldval: string;
  newval: string;
  type: string;
  otherid: string;
  /** Resolved display name of the SELECTED user this row belongs to. */
  user_name: string;
  report_user: string;
}

/** A telemetry event — user_activity. Sampled from the browser. */
export interface ActivityReportRow {
  id: number;
  user_id: string;
  user_name: string;
  user_email: string;
  event_type: string;
  occurred_at: string;
  page_path: string;
  target: string | null;
  detail: string | null;
  duration_ms: number | null;
  session_id: string | null;
}

interface BaseResponse {
  start_date: string | null;
  end_date: string | null;
  users: ReportUser[];
  /** Rows returned. There is NO server-side cap: this is the complete answer for the
   *  selected people and window, and the count is here only so the page can warn when
   *  a result is large enough to be slow to render. */
  row_count: number;
}

export interface HistoryReportResponse extends BaseResponse {
  items: HistoryReportRow[];
}

export interface ActivityReportResponse extends BaseResponse {
  items: ActivityReportRow[];
  /** Oldest row retained for the SELECTED users, so a range reaching past
   *  retention can be labelled as such instead of reading as "did nothing". */
  activity_oldest: string | null;
}

export type SeriesBucket = 'day' | 'hour';
export type SeriesSource = 'history' | 'activity';

/** One person's hit count for one time bucket. Counted in SQL — see the /series
 *  endpoint on why this is never derived from the detail rows. */
export interface SeriesPoint {
  report_user: string;
  user_name: string;
  bucket: string;
  hits: number;
}

export interface SeriesResponse {
  source: SeriesSource;
  bucket: SeriesBucket;
  start_date: string | null;
  end_date: string | null;
  users: ReportUser[];
  points: SeriesPoint[];
}

export interface ReportQuery {
  userIds: string[];
  startDate?: string;
  endDate?: string;
}

const params = (q: ReportQuery) => ({
  user_ids: q.userIds.join(','),
  start_date: q.startDate,
  end_date: q.endDate,
});

const userHistoryReportService = {
  listUsers: async (signal?: AbortSignal): Promise<ReportUsersResponse> => {
    const res = await api.get<ReportUsersResponse>('/user-history-report/users', { signal });
    return { users: res.data.users ?? [], groups: res.data.groups ?? [] };
  },

  history: async (q: ReportQuery, signal?: AbortSignal): Promise<HistoryReportResponse> => {
    const res = await api.get<HistoryReportResponse>('/user-history-report/history', {
      params: params(q), signal,
    });
    return res.data;
  },

  series: async (
    q: ReportQuery & { source: SeriesSource; bucket: SeriesBucket },
    signal?: AbortSignal,
  ): Promise<SeriesResponse> => {
    const res = await api.get<SeriesResponse>('/user-history-report/series', {
      params: { ...params(q), source: q.source, bucket: q.bucket }, signal,
    });
    return res.data;
  },

  activity: async (q: ReportQuery, signal?: AbortSignal): Promise<ActivityReportResponse> => {
    const res = await api.get<ActivityReportResponse>('/user-history-report/activity', {
      params: params(q), signal,
    });
    return res.data;
  },
};

export default userHistoryReportService;
